import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLanguage } from "./Language";
import axios from "axios";

export interface ResponseStatus {
  success: boolean;
  message: string;
  timeout: boolean;
}

export interface AbstractBaseResponse {
  status: ResponseStatus;
}

interface SendResetPINEmailResponse extends AbstractBaseResponse {}

export interface ForgotPINDialogProps {
  onClose: () => void;
}

export const ForgotPINDialog: React.FC<ForgotPINDialogProps> = ({
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState(true);
  const [success, setSuccess] = React.useState(false);
  const [barcode, setBarcode] = React.useState("");
  const [barcodeInvalid, setBarcodeInvalid] = React.useState(true);
  const [barcodeHelperText, setBarcodeHelperText] = React.useState(" ");
  const [barcodeTouched, setBarcodeTouched] = React.useState(false);
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  const { translate } = useLanguage();
  
  // requiredFieldError must be included in useEffect deps to handle language changes
  const requiredFieldError = translate("requiredFieldError");

  React.useEffect(() => {
    if (barcodeInvalid && (barcodeTouched || formSubmitted)) {
      setBarcodeHelperText(translate("requiredFieldError"));
    } else {
      setBarcodeHelperText(" ");
    }
  }, [barcodeInvalid, barcodeTouched, formSubmitted, requiredFieldError]);

  const validateBarcode = (barcodeInput: string) => {
    const isInvalid = barcodeInput.trim().length === 0;
    setBarcodeInvalid(isInvalid);
    return isInvalid;
  }

  const onChangeBarcode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const barcode = event.target.value;
    setBarcode(barcode);
    validateBarcode(barcode);
  };

  const onBlurBarcode = (event: React.FocusEvent<HTMLInputElement>) => {
    const barcode = event.target.value;
    setBarcodeTouched(true);
    validateBarcode(barcode);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormSubmitted(true);
    const isInvalid = validateBarcode(barcode);

    if (isInvalid) {
      console.log("Form has invalid fields");
      return;
    }

    let success = false;
    axios
      .get<SendResetPINEmailResponse>("/account/sendPinResetEmail", {
        params: {
          borrowerId: barcode,
        },
      })
      .then((response) => {
        success = response.data.status.success === true;
      })
      .catch((error) => {
        console.error("An unexpected error occurred:", error);

      }).finally(() => {
        setSuccess(true);
      });
  };

  const handleCancel = () => {
    setOpen(false);
    onClose();
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  }

  return (
    <Dialog open={open} fullScreen={fullScreen}>
      {success && (
        <React.Fragment>
          <DialogTitle>{translate("forgotPIN")}</DialogTitle>
          <DialogContent>
            <Alert severity="success">
              {translate("requestPINResetResponse")}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{translate("ok")}</Button>
          </DialogActions>
        </React.Fragment>
      )}

      {!success && (
        <form onSubmit={handleSubmit} noValidate>
          <DialogTitle>{translate("forgotPIN")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {translate("forgotPINFormInstructions")}
            </DialogContentText>
            <Stack gap={2} paddingTop={2}>
              <TextField
                name="barcode"
                required
                value={barcode}
                label={translate("barcode")}
                autoFocus
                error={barcodeInvalid && (barcodeTouched || formSubmitted)}
                helperText={barcodeHelperText}
                id="forgot-pin-barcode"
                onChange={onChangeBarcode}
                onBlur={onBlurBarcode}
              ></TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel}>{translate("cancel")}</Button>
            <Button type="submit">{translate("resetPIN")}</Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
